import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Building2, Users, Activity, Receipt, ArrowRight, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { tenantService } from "@/entities/tenant";
import { cn } from "@/shared/lib/utils";
import { useStaggerReveal } from "@/shared/motion/useStaggerReveal";
import {
  CARD_WALL,
  CARD_WALL_QUAD,
  CARD_WALL_RADIUS,
  clickableCardProps,
} from "@/shared/ui/cardWall";

/**
 * 平台超管首页 — 全局运营总览。
 *
 * MVP:租户总数 / 启用 / 试用 / 即将到期。后续追加全局 AI 调用量、
 * 收入、ai-service 健康、告警等。
 *
 * 2026-07-25 G1 铺量批:指标行上卡墙规格。
 *  - 栅格从视口断点(`sm:2 lg:4`)改容器查询 `cardwall-quad`:实测原版视口 1040 处容器仅 736
 *    却已越过 `lg`(1024)→ 4 列 **160px**;而 320~1380 有 24 档跌在 250 以下。
 *    quad 档只取张数的约数列,4 张指标卡任何宽度下都是整行。
 *  - 圆角走形状锁单源(原来 `<Card>` 默认 `rounded-lg` = `--radius × 1`,比卡墙其余卡都平;
 *    页头横幅的 `rounded-2xl` 更是写死 16px,三皮肤的圆角语言全被它抹平)。
 *  - 入场统一走 `useStaggerReveal`。
 *  ★下方「快捷入口 / 财务概览」两块**不是卡墙**,是 2 栏面板布局(每块内含链接列表),
 *    没有按 quad 档处理 —— 把面板塞进约数列会在窄容器下压成 162px,链接文案直接换行。
 */
export default function PlatformHomePage() {
  const { t } = useTranslation("platform");
  const navigate = useNavigate();
  const kpiRef = useRef<HTMLDivElement>(null);
  const {
    data: summary,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["platform-summary"],
    queryFn: tenantService.summary,
    meta: { suppressGlobalErrorToast: true },
  });

  useStaggerReveal(kpiRef, { deps: [summary ? 1 : 0] });

  // audit-0713 C1b:加载失败不再渲染「租户总数 0」假数据 —— 失败显「–」+ 页内错误横幅
  const cards = [
    {
      label: t("home.totalTenants"),
      value: summary ? summary.total : "–",
      icon: Building2,
      tone: "text-primary",
      onClick: () => navigate("/tenants"),
    },
    {
      label: t("home.active"),
      value: summary ? summary.active : "–",
      icon: Users,
      tone: "text-success",
    },
    {
      label: t("home.trial"),
      value: summary ? summary.trial : "–",
      icon: Activity,
      tone: "text-info",
    },
    {
      label: t("home.expiring"),
      value: summary ? summary.expiring : "–",
      icon: Receipt,
      tone: "text-warning",
    },
  ];

  return (
    <>
      <div className="px-6 py-6 space-y-6">
        <div
          className={cn(
            "border bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6",
            CARD_WALL_RADIUS
          )}
        >
          <h1 className="text-2xl font-bold text-foreground">{t("home.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("home.subtitle")}</p>
        </div>

        <QueryErrorBanner isError={isError} onRetry={() => void refetch()} />

        <div className={CARD_WALL}>
          <div ref={kpiRef} className={CARD_WALL_QUAD}>
            {cards.map((c) => (
              <Card
                key={c.label}
                data-kpi-card={c.label}
                className={cn(
                  CARD_WALL_RADIUS,
                  c.onClick &&
                    "fx-hover-lift fx-press cursor-pointer transition-shadow hover:shadow-md " +
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                )}
                // ★可点的那张(租户总数)原来只有 onClick,键盘 Tab 永远到不了(门 H-D 实测)
                {...(c.onClick ? clickableCardProps(c.onClick) : {})}
              >
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">{c.label}</p>
                      <p className="text-2xl font-bold mt-1">
                        {isLoading ? (
                          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        ) : (
                          c.value
                        )}
                      </p>
                    </div>
                    <c.icon className={`h-8 w-8 ${c.tone} opacity-70`} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("home.quickEntries")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <QuickLink
                label={t("home.tenantMgmt")}
                desc={t("home.tenantMgmtDesc")}
                onClick={() => navigate("/tenants")}
              />
              <QuickLink
                label={t("home.planCatalog")}
                desc={t("home.planCatalogDesc")}
                onClick={() => navigate("/tenant-plans")}
              />
              <QuickLink
                label={t("home.channelsOverview")}
                desc={t("home.channelsOverviewDesc")}
                onClick={() => navigate("/platform/channels")}
              />
              <QuickLink
                label={t("home.auditLog")}
                desc={t("home.auditLogDesc")}
                onClick={() => navigate("/platform/audit")}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("home.financeOverview")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <QuickLink
                label={t("home.financeBoard")}
                desc={t("home.financeBoardDesc")}
                onClick={() => navigate("/admin/billing-dashboard")}
              />
              <QuickLink
                label={t("home.billingLedger")}
                desc={t("home.billingLedgerDesc")}
                onClick={() => navigate("/admin/billing-ledger")}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}

function QuickLink({ label, desc, onClick }: { label: string; desc: string; onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      className="w-full justify-between h-auto py-3 px-3 text-left"
      onClick={onClick}
    >
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
      <ArrowRight className="h-4 w-4 text-muted-foreground" />
    </Button>
  );
}
